import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { PhotoUpload } from "@/shared/components/common/PhotoUpload";
import { ClientSearchInput } from "./ClientSearchInput";
import type { ClientOption } from "./ClientSearchInput";
import { ContractParentSearchInput } from "./ContractParentSearchInput";
import type { ContractParentOption } from "../types/contract.types";
import type { UseFormReturn } from "react-hook-form";
import type { ContractFormData } from "../types/contract.types";
import { memo } from "react";

/**
 * Dialog de criação e edição de contrato.
 *
 * Completamente controlado: todo o estado (form, buscas, preview) vive na página
 * e é injetado via props. `memo` evita re-renders causados por mudanças de estado
 * não relacionadas na página pai.
 *
 * @example
 * <ContractFormDialog
 *   open={dialogOpen}
 *   mode="create"
 *   loading={saving}
 *   form={form}
 *   photoPreview={photoPreview}
 *   onClose={handleClose}
 *   onSubmit={handleSubmit}
 *   onFileChange={handleFileChange}
 *   onRemoveImage={handleRemoveImage}
 *   {...clientSearchProps}
 *   {...parentSearchProps}
 * />
 */
interface ContractFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  loading: boolean;
  form: UseFormReturn<ContractFormData>;
  photoPreview: string | null;
  onClose: () => void;
  onSubmit: (data: ContractFormData) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage: () => void;
  // client search
  clientSearchTerm: string;
  clientSearchOptions: ClientOption[];
  clientSearchLoading: boolean;
  selectedClientId: number | null;
  onClientSearch: (term: string) => void;
  onClientSelect: (client: ClientOption) => void;
  onClientClear: () => void;
  // parent contract search
  parentSearchTerm: string;
  parentSearchOptions: ContractParentOption[];
  parentSearchLoading: boolean;
  selectedParentId: number | null;
  onParentSearch: (term: string) => void;
  onParentSelect: (contract: ContractParentOption) => void;
  onParentClear: () => void;
}

export const ContractFormDialog = memo(function ContractFormDialog({
  open,
  mode,
  loading,
  form,
  photoPreview,
  onClose,
  onSubmit,
  onFileChange,
  onRemoveImage,
  clientSearchTerm,
  clientSearchOptions,
  clientSearchLoading,
  selectedClientId,
  onClientSearch,
  onClientSelect,
  onClientClear,
  parentSearchTerm,
  parentSearchOptions,
  parentSearchLoading,
  selectedParentId,
  onParentSearch,
  onParentSelect,
  onParentClear,
}: ContractFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Novo Contrato" : "Editar Contrato"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Preencha os dados do novo contrato"
              : "Atualize as informações do contrato"}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="contractNumber"
              render={({ field }) => (
                <FormItem>
                  <label className="text-sm font-medium">
                    Número do Contrato *
                  </label>
                  <Input placeholder="CTR-2024-001" {...field} />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientId"
              render={() => (
                <FormItem>
                  <label className="text-sm font-medium">Cliente *</label>
                  <ClientSearchInput
                    value={clientSearchTerm}
                    selectedId={selectedClientId}
                    options={clientSearchOptions}
                    loading={clientSearchLoading}
                    onSearch={onClientSearch}
                    onSelect={onClientSelect}
                    onClear={onClientClear}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="parentingId"
              render={() => (
                <FormItem>
                  <label className="text-sm font-medium">Contrato Pai</label>
                  <ContractParentSearchInput
                    value={parentSearchTerm}
                    selectedId={selectedParentId}
                    options={parentSearchOptions}
                    loading={parentSearchLoading}
                    onSearch={onParentSearch}
                    onSelect={onParentSelect}
                    onClear={onParentClear}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contractImage"
              render={() => (
                <PhotoUpload
                  label="Imagem do Contrato"
                  photoPreview={photoPreview}
                  onFileChange={onFileChange}
                  onRemovePhoto={onRemoveImage}
                  inputId="contract-image-upload"
                />
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading
                  ? "Salvando..."
                  : mode === "create"
                    ? "Criar Contrato"
                    : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
